// Staff PIN and staff devices (issue #12, US-9 and US-39). Pure functions, no I/O.
// A manager generates the venue's 6-digit PIN; it is shown once and stored hashed (the hash itself is computed in
// the API, venue-scoped). Every browser that enters the PIN becomes a "staff device": a row with a label, first and
// last seen, a 30-day expiry and a revocation mark. The API checks that row on every staff request and the open
// staff screen checks in every `checkEveryMs`, so a revoked device is back on the PIN form within a minute.
import { generateLoginCode, parseLoginCode } from './auth';
import { UI, fill } from './copy';
import type { Lang } from './ticket';

export const STAFF_PIN = {
  /** Six digits, like the sign-in code: short enough to read out, long enough with the five-attempt lock. */
  length: 6,
} as const;

export const STAFF_DEVICE = {
  /** A device that entered the PIN stays signed in for 30 days unless revoked (US-39). */
  ttlMs: 30 * 24 * 60 * 60_000,
  /** How often an open staff screen asks the server whether its session still stands. */
  checkEveryMs: 30_000,
  /** The promise of the issue: a revoked device loses the staff screen within a minute. */
  revokeWithinMs: 60_000,
  /** `last_seen_at` is written back at most this often, so a screen polling every 30 s costs one write a minute. */
  touchEveryMs: 60_000,
  /** Former devices (revoked or expired) are listed this long, then deleted by the nightly cron. */
  keepFormerMs: 30 * 24 * 60 * 60_000,
} as const;

/** Six uniform random digits, leading zeros kept: the same generator as the sign-in code. */
export function generateStaffPin(): string {
  return generateLoginCode();
}

/** The PIN as typed on the staff screen: spaces and Arabic-Indic digits accepted; null unless exactly six digits. */
export function parseStaffPin(input: string): string | null {
  return parseLoginCode(input);
}

export type StaffDeviceStatus = 'active' | 'revoked' | 'expired';

/** Why a device lost access: a manager revoked it, the PIN was regenerated, or the same browser signed in again. */
export type StaffDeviceRevokeReason = 'revoked' | 'pin_regenerated' | 'replaced';

export interface StaffDeviceLike {
  expiresAt: number;
  revokedAt: number | null;
}

export function staffDeviceExpiresAt(signedInAt: number): number {
  return signedInAt + STAFF_DEVICE.ttlMs;
}

/** Revoked wins over expired, so the list says why the device went away. */
export function staffDeviceStatus(d: StaffDeviceLike, now: number): StaffDeviceStatus {
  if (d.revokedAt !== null) return 'revoked';
  if (now >= d.expiresAt) return 'expired';
  return 'active';
}

/** The one check the API makes on every staff request: the device row is neither revoked nor past its 30 days. */
export function isStaffDeviceAlive(d: StaffDeviceLike, now: number): boolean {
  return staffDeviceStatus(d, now) === 'active';
}

export function shouldTouchStaffDevice(lastSeenAt: number, now: number): boolean {
  return now - lastSeenAt >= STAFF_DEVICE.touchEveryMs;
}

// ---- labels ----

/**
 * A short, language-neutral name for the device from its user agent ("iPhone · Safari", "Android · Chrome"),
 * enough for a manager to tell the counter tablet from a cashier's phone. Null when nothing can be read from it;
 * the dashboard then shows `unknown_device`. The user agent itself is never shown.
 */
export function deviceLabel(userAgent: string | null | undefined): string | null {
  const ua = userAgent ?? '';
  if (ua.trim() === '') return null;
  let os: string | null = null;
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && /Mobile/.test(ua))) os = 'iPad';
  else if (/iPhone|iPod/.test(ua)) os = 'iPhone';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/CrOS/.test(ua)) os = 'Chromebook';
  else if (/Windows/.test(ua)) os = 'Windows';
  else if (/Macintosh|Mac OS X/.test(ua)) os = 'Mac';
  else if (/Linux/.test(ua)) os = 'Linux';
  let browser: string | null = null;
  if (/Edg(e|A|iOS)?\//.test(ua)) browser = 'Edge';
  else if (/SamsungBrowser\//.test(ua)) browser = 'Samsung Internet';
  else if (/OPR\/|Opera/.test(ua)) browser = 'Opera';
  else if (/FxiOS|Firefox\//.test(ua)) browser = 'Firefox';
  else if (/CriOS|Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua) && /Version\//.test(ua)) browser = 'Safari';
  const parts = [os, browser].filter((p): p is string => p !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
}

// ---- copy ----

/** "à l'instant", "il y a 5 min", "il y a 3 h", "il y a 2 j": coarse on purpose, the list is read at a glance. */
export function lastSeenCopy(lang: Lang, lastSeenAt: number, now: number): string {
  const t = UI[lang];
  const ago = Math.max(0, now - lastSeenAt);
  if (ago < 60_000) return t.seen_just_now;
  const minutes = Math.floor(ago / 60_000);
  if (minutes < 60) return fill(t.seen_minutes_ago, { minutes: String(minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return fill(t.seen_hours_ago, { hours: String(hours) });
  return fill(t.seen_days_ago, { days: String(Math.floor(hours / 24)) });
}

/** The status tag of a device: a symbol and words, never color alone. */
export function deviceStatusCopy(lang: Lang, status: StaffDeviceStatus, reason: StaffDeviceRevokeReason | string | null): { symbol: string; label: string } {
  const t = UI[lang];
  if (status === 'active') return { symbol: '✓', label: t.device_active };
  if (status === 'expired') return { symbol: '⏱', label: t.device_expired };
  if (reason === 'pin_regenerated') return { symbol: '✕', label: t.device_signed_out_pin };
  if (reason === 'replaced') return { symbol: '↻', label: t.device_replaced };
  return { symbol: '✕', label: t.device_revoked };
}

/**
 * What the dashboard says about the PIN: none yet, the date it was set, or just "set" for a PIN that predates the
 * dashboard (the seed). The PIN itself is never readable again.
 */
export function staffPinStatusCopy(lang: Lang, hasPin: boolean, setAt: number | null, formatDate: (ts: number) => string): string {
  const t = UI[lang];
  if (!hasPin) return t.no_staff_pin;
  return setAt === null ? t.staff_pin_set : fill(t.staff_pin_set_on, { date: formatDate(setAt) });
}

/** The warning before regenerating: how many devices lose the screen. */
export function regeneratePinWarning(lang: Lang, activeDevices: number): string {
  return fill(UI[lang].regenerate_warning, { count: String(activeDevices) });
}
